import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddBusinessRoundedIcon from "@mui/icons-material/AddBusinessRounded";
import RecyclingRoundedIcon from "@mui/icons-material/RecyclingRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { api } from "../../lib/api";

const GREEN = "#16794a";
const blankCompany = {
  name: "",
  slug: "",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  address: "",
  postalCode: "",
  city: "",
  country: "France",
  notes: "",
};
const blankCollection = {
  companyId: "",
  title: "",
  slug: "",
  status: "preparation",
  startDate: "",
  endDate: "",
  locationLabel: "",
  introText: "",
  employeeMessage: "",
  heroImageUrl: "",
  collectionImageUrl: "",
  reportImageUrl: "",
  phonesCount: 0,
  tabletsCount: 0,
  laptopsCount: 0,
  otherDevicesCount: 0,
  depollutedPartsCount: 0,
  recycledPartsCount: 0,
  reusedPartsCount: 0,
  reportMessage: "",
};
const statusLabel = {
  preparation: "Préparation",
  active: "Collecte en cours",
  report: "Rapport publié",
};
const publicUrl = (slug) => `https://collect.123back.eu/${slug}`;

export default function Collects() {
  const [tab, setTab] = React.useState(0);
  const [companies, setCompanies] = React.useState([]);
  const [collections, setCollections] = React.useState([]);
  const [inquiries, setInquiries] = React.useState([]);
  const [companyOpen, setCompanyOpen] = React.useState(false);
  const [companyForm, setCompanyForm] = React.useState(blankCompany);
  const [companyId, setCompanyId] = React.useState("");
  const [companySourceInquiryId, setCompanySourceInquiryId] =
    React.useState("");
  const [collectionOpen, setCollectionOpen] = React.useState(false);
  const [collectionStep, setCollectionStep] = React.useState(0);
  const [collectionForm, setCollectionForm] = React.useState(blankCollection);
  const [collectionId, setCollectionId] = React.useState("");
  const [collectionSearch, setCollectionSearch] = React.useState("");
  const [collectionStatus, setCollectionStatus] = React.useState("all");
  const [message, setMessage] = React.useState(null);
  const [uploading, setUploading] = React.useState("");
  const load = React.useCallback(async () => {
    try {
      const [a, b, c] = await Promise.all([
        api.get("/collect/companies"),
        api.get("/collect/collections"),
        api.get("/collect/inquiries"),
      ]);
      setCompanies(a.data || []);
      setCollections(b.data || []);
      setInquiries(c.data || []);
    } catch (e) {
      setMessage({
        type: "error",
        text: e?.response?.data?.message || "Chargement impossible.",
      });
    }
  }, []);
  React.useEffect(() => {
    load();
  }, [load]);
  const changeCompany = (key) => (e) =>
    setCompanyForm((x) => ({ ...x, [key]: e.target.value }));
  const changeCollection = (key) => (e) =>
    setCollectionForm((x) => ({ ...x, [key]: e.target.value }));
  const openCompany = (row = null) => {
    setCompanySourceInquiryId("");
    setCompanyId(row?.id || "");
    setCompanyForm(
      row
        ? Object.fromEntries(
            Object.keys(blankCompany).map((k) => [
              k,
              row[k] ?? blankCompany[k],
            ]),
          )
        : blankCompany,
    );
    setCompanyOpen(true);
  };
  const companyFromInquiry = (row) => {
    setCompanySourceInquiryId(row.id);
    setCompanyId("");
    setCompanyForm({
      ...blankCompany,
      name: row.companyName,
      contactName: row.contactName,
      contactEmail: row.email,
      contactPhone: row.phone || "",
      city: row.city || "",
    });
    setCompanyOpen(true);
  };
  const saveCompany = async () => {
    try {
      const response = companyId
        ? await api.put(`/collect/companies/${companyId}`, companyForm)
        : await api.post("/collect/companies", companyForm);
      if (companySourceInquiryId)
        await api.patch(`/collect/inquiries/${companySourceInquiryId}`, {
          status: "contacted",
        });
      setCompanyOpen(false);
      await load();
      if (companySourceInquiryId) {
        setTab(0);
        setCollectionId("");
        setCollectionForm({
          ...blankCollection,
          companyId: response.data.id,
          title: `Collecte ${response.data.name}`,
        });
        setCollectionOpen(true);
        setCompanySourceInquiryId("");
        setMessage({
          type: "success",
          text: "Entreprise créée. Tu peux maintenant préparer sa page de collecte.",
        });
      } else {
        setMessage({ type: "success", text: "Fiche entreprise enregistrée." });
      }
    } catch (e) {
      setMessage({
        type: "error",
        text: e?.response?.data?.message || "Enregistrement impossible.",
      });
    }
  };
  const openCollection = (row = null, company = null) => {
    setCollectionId(row?.id || "");
    setCollectionForm(
      row
        ? Object.fromEntries(
            Object.keys(blankCollection).map((k) => [
              k,
              row[k] ?? blankCollection[k],
            ]),
          )
        : {
            ...blankCollection,
            companyId: company?.id || "",
            title: company ? `Collecte ${company.name}` : "",
          },
    );
    setCollectionStep(row?.status === "report" ? 2 : 0);
    setCollectionOpen(true);
  };
  const saveCollection = async () => {
    try {
      const response = collectionId
        ? await api.put(`/collect/collections/${collectionId}`, collectionForm)
        : await api.post("/collect/collections", collectionForm);
      const url = publicUrl(response.data.slug);
      setCollectionOpen(false);
      setMessage({
        type: "success",
        text: "Collecte enregistrée. Le lien public est prêt.",
        url,
      });
      await load();
    } catch (e) {
      setMessage({
        type: "error",
        text: e?.response?.data?.message || "Enregistrement impossible.",
      });
    }
  };
  const uploadImage = async (event, field) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(field);
    try {
      const body = new FormData();
      body.append("image", file);
      const { data } = await api.post("/collect/images", body);
      setCollectionForm((x) => ({ ...x, [field]: data.url }));
    } catch (e) {
      setMessage({
        type: "error",
        text: e?.response?.data?.message || "Envoi de l’image impossible.",
      });
    } finally {
      setUploading("");
      event.target.value = "";
    }
  };
  const copy = async (slug) => {
    await navigator.clipboard.writeText(publicUrl(slug));
    setMessage({ type: "success", text: "Lien copié." });
  };
  const startCollection = async (row) => {
    try {
      await api.put(`/collect/collections/${row.id}`, { status: "active" });
      setMessage({
        type: "success",
        text: "La collecte est maintenant affichée comme étant en cours.",
      });
      await load();
    } catch (e) {
      setMessage({
        type: "error",
        text: e?.response?.data?.message || "Mise à jour impossible.",
      });
    }
  };
  const prepareReport = (row) => {
    openCollection({ ...row, status: "report" });
  };
  const republishReport = async (row) => {
    try {
      const { data } = await api.post(
        `/collect/collections/${row.id}/republish`,
      );
      setMessage({
        type: "success",
        text: "Le rapport est de nouveau accessible pendant 30 jours.",
        url: publicUrl(data.slug),
      });
      await load();
    } catch (e) {
      setMessage({
        type: "error",
        text: e?.response?.data?.message || "Republication impossible.",
      });
    }
  };
  const setInquiryStatus = async (id, status) => {
    try {
      await api.patch(`/collect/inquiries/${id}`, { status });
      await load();
    } catch (e) {
      setMessage({
        type: "error",
        text: e?.response?.data?.message || "Mise à jour impossible.",
      });
    }
  };
  const deleteInquiry = async (row) => {
    if (
      !window.confirm(
        `Supprimer définitivement la demande de ${row.companyName} ?`,
      )
    )
      return;
    try {
      await api.delete(`/collect/inquiries/${row.id}`);
      setMessage({ type: "success", text: "La demande a été supprimée." });
      await load();
    } catch (e) {
      setMessage({
        type: "error",
        text: e?.response?.data?.message || "Suppression impossible.",
      });
    }
  };
  const visibleCollections = React.useMemo(() => {
    const query = collectionSearch.trim().toLowerCase();
    return collections.filter((row) => {
      if (collectionStatus !== "all" && row.status !== collectionStatus)
        return false;
      if (!query) return true;
      return [row.title, row.company?.name, row.locationLabel, row.slug].some(
        (value) =>
          String(value || "")
            .toLowerCase()
            .includes(query),
      );
    });
  }, [collections, collectionSearch, collectionStatus]);
  return (
    <Stack spacing={3}>
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          gap={2}
        >
          <Box>
            <Typography variant="h5" fontWeight={900}>
              Collect
            </Typography>
            <Typography color="text.secondary">
              Gère les entreprises, les pages de collecte et les rapports RSE.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<AddBusinessRoundedIcon />}
              variant="outlined"
              onClick={() => openCompany()}
            >
              Entreprise
            </Button>
            <Button
              startIcon={<RecyclingRoundedIcon />}
              variant="contained"
              onClick={() => openCollection()}
              sx={{ bgcolor: GREEN }}
            >
              Nouvelle collecte
            </Button>
          </Stack>
        </Stack>
        {message && (
          <Alert
            severity={message.type}
            onClose={() => setMessage(null)}
            sx={{ mt: 2 }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ sm: "center" }}
              gap={1}
            >
              <Typography sx={{ flex: 1 }}>{message.text}</Typography>
              {message.url && (
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<ContentCopyRoundedIcon />}
                  onClick={() => navigator.clipboard.writeText(message.url)}
                >
                  Copier le lien
                </Button>
              )}
            </Stack>
          </Alert>
        )}
        <Tabs
          value={tab}
          onChange={(_e, value) => setTab(value)}
          sx={{ mt: 2 }}
        >
          <Tab label={`Collectes (${collections.length})`} />
          <Tab label={`Entreprises (${companies.length})`} />
          <Tab
            label={`Demandes (${inquiries.filter((x) => x.status === "new").length})`}
          />
        </Tabs>
      </Paper>
      <Box
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: 2,
        }}
      >
        {[
          [
            "En préparation",
            collections.filter((x) => x.status === "preparation").length,
          ],
          ["En cours", collections.filter((x) => x.status === "active").length],
          [
            "Rapports disponibles",
            collections.filter(
              (x) =>
                x.status === "report" &&
                (!x.reportExpiresAt ||
                  new Date(x.reportExpiresAt) > new Date()),
            ).length,
          ],
          [
            "Nouvelles demandes",
            inquiries.filter((x) => x.status === "new").length,
          ],
        ].map(([label, value]) => (
          <Paper
            key={label}
            variant="outlined"
            sx={{ p: 2.5, borderRadius: 3, minWidth: 0 }}
          >
            <Typography color="text.secondary" variant="body2">
              {label}
            </Typography>
            <Typography color={GREEN} fontSize={30} fontWeight={900}>
              {value}
            </Typography>
          </Paper>
        ))}
      </Box>
      {tab === 0 && (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ md: "center" }}
            >
              <TextField
                size="small"
                fullWidth
                label="Rechercher une entreprise ou une collecte"
                value={collectionSearch}
                onChange={(event) => setCollectionSearch(event.target.value)}
              />
              <TextField
                select
                size="small"
                label="Étape"
                value={collectionStatus}
                onChange={(event) => setCollectionStatus(event.target.value)}
                sx={{ minWidth: 210 }}
              >
                <MenuItem value="all">Toutes les étapes</MenuItem>
                <MenuItem value="preparation">En préparation</MenuItem>
                <MenuItem value="active">Collecte en cours</MenuItem>
                <MenuItem value="report">Rapport publié</MenuItem>
              </TextField>
              <Typography
                variant="body2"
                fontWeight={800}
                color="text.secondary"
                sx={{ whiteSpace: "nowrap" }}
              >
                {visibleCollections.length} résultat(s)
              </Typography>
            </Stack>
          </Paper>
          {collections.length === 0 && (
            <Alert severity="info">
              Commence par créer une entreprise, puis crée sa première collecte.
            </Alert>
          )}
          {collections.length > 0 && visibleCollections.length === 0 && (
            <Alert severity="info">
              Aucune collecte ne correspond à cette recherche.
            </Alert>
          )}
          {visibleCollections.map((row) => {
            const total =
              row.phonesCount +
              row.tabletsCount +
              row.laptopsCount +
              row.otherDevicesCount;
            const expired =
              row.reportExpiresAt &&
              new Date(row.reportExpiresAt) <= new Date();
            return (
              <Paper
                key={row.id}
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  borderLeft: "5px solid",
                  borderLeftColor:
                    row.status === "report"
                      ? "success.main"
                      : row.status === "active"
                        ? "warning.main"
                        : "grey.400",
                }}
              >
                <Stack direction="column" gap={2}>
                  <Box>
                    <Stack
                      direction="row"
                      gap={1}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <Typography variant="h6" fontWeight={900}>
                        {row.title}
                      </Typography>
                      <Chip
                        label={statusLabel[row.status]}
                        color={
                          row.status === "report"
                            ? "success"
                            : row.status === "active"
                              ? "warning"
                              : "default"
                        }
                        size="medium"
                      />
                      <Chip
                        label={row.company?.name || "Entreprise"}
                        variant="outlined"
                        size="medium"
                      />
                      {expired && (
                        <Chip label="Lien expiré" color="error" size="small" />
                      )}
                    </Stack>
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                      {row.locationLabel || "Lieu à renseigner"} · {total}{" "}
                      appareil(s) dans le rapport
                    </Typography>
                    <Typography
                      color={GREEN}
                      fontWeight={800}
                      sx={{ mt: 1, wordBreak: "break-all" }}
                    >
                      {publicUrl(row.slug)}
                    </Typography>
                    {row.status === "report" && row.reportExpiresAt && (
                      <Typography variant="caption" color="text.secondary">
                        Rapport accessible jusqu’au{" "}
                        {new Intl.DateTimeFormat("fr-FR", {
                          dateStyle: "long",
                        }).format(new Date(row.reportExpiresAt))}
                      </Typography>
                    )}
                    <Stack
                      direction="row"
                      spacing={0.75}
                      sx={{ mt: 1.5 }}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {["Préparation", "Collecte en cours", "Bilan"].map(
                        (label, index) => {
                          const currentIndex = [
                            "preparation",
                            "active",
                            "report",
                          ].indexOf(row.status);
                          return (
                            <Chip
                              key={label}
                              size="small"
                              label={`${index + 1}. ${label}`}
                              variant={
                                index <= currentIndex ? "filled" : "outlined"
                              }
                              sx={
                                index <= currentIndex
                                  ? {
                                      bgcolor: "#e3f3e8",
                                      color: GREEN,
                                      fontWeight: 800,
                                    }
                                  : {}
                              }
                            />
                          );
                        },
                      )}
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      Créée le{" "}
                      {new Intl.DateTimeFormat("fr-FR", {
                        dateStyle: "short",
                      }).format(new Date(row.createdAt))}{" "}
                      · dernière modification le{" "}
                      {new Intl.DateTimeFormat("fr-FR", {
                        dateStyle: "short",
                        timeStyle: "short",
                      }).format(new Date(row.updatedAt))}
                    </Typography>
                  </Box>
                  <Divider />
                  <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
                    <Button
                      size="small"
                      startIcon={<ContentCopyRoundedIcon />}
                      onClick={() => copy(row.slug)}
                    >
                      Copier
                    </Button>
                    <Button
                      size="small"
                      startIcon={<OpenInNewRoundedIcon />}
                      href={publicUrl(row.slug)}
                      target="_blank"
                    >
                      Voir
                    </Button>
                    <Button
                      size="small"
                      startIcon={<EditRoundedIcon />}
                      onClick={() => openCollection(row)}
                    >
                      Modifier
                    </Button>
                    {row.status === "preparation" && (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<PlayArrowRoundedIcon />}
                        onClick={() => startCollection(row)}
                      >
                        Démarrer
                      </Button>
                    )}
                    {row.status === "active" && (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<AssessmentRoundedIcon />}
                        onClick={() => prepareReport(row)}
                      >
                        Compléter le bilan
                      </Button>
                    )}
                    {row.status === "report" && expired && (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<RefreshRoundedIcon />}
                        onClick={() => republishReport(row)}
                      >
                        Republier 30 jours
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
      {tab === 1 && (
        <Grid container spacing={2}>
          {companies.map((row) => (
            <Grid item xs={12} key={row.id}>
              <Paper
                variant="outlined"
                sx={{ p: 2.5, borderRadius: 3, height: "100%" }}
              >
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    flexWrap="wrap"
                  >
                    <Typography variant="h6" fontWeight={900}>
                      {row.name}
                    </Typography>
                    <Chip
                      label={`${row.collections?.length || 0} collecte(s)`}
                      size="small"
                      sx={{ bgcolor: "#e3f3e8", color: GREEN, fontWeight: 800 }}
                    />
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary">
                        Contact
                      </Typography>
                      <Typography fontWeight={700}>
                        {row.contactName || "Non renseigné"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary">
                        Coordonnées
                      </Typography>
                      <Typography sx={{ wordBreak: "break-word" }}>
                        {row.contactEmail || "—"} · {row.contactPhone || "—"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary">
                        Adresse
                      </Typography>
                      <Typography>
                        {[row.address, row.postalCode, row.city, row.country]
                          .filter(Boolean)
                          .join(", ") || "Non renseignée"}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider />
                  <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => openCollection(null, row)}
                      startIcon={<RecyclingRoundedIcon />}
                    >
                      Nouvelle collecte
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => openCompany(row)}
                      startIcon={<EditRoundedIcon />}
                    >
                      Modifier l’entreprise
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
      {tab === 2 && (
        <Stack spacing={2}>
          {inquiries.length === 0 && (
            <Alert severity="info">Aucune demande reçue pour le moment.</Alert>
          )}
          {inquiries.map((row) => (
            <Paper
              key={row.id}
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 3,
                borderLeft: "5px solid",
                borderLeftColor:
                  row.status === "new"
                    ? "error.main"
                    : row.status === "contacted"
                      ? "warning.main"
                      : "success.main",
              }}
            >
              <Stack direction="column" gap={2}>
                <Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Typography variant="h6" fontWeight={900}>
                      {row.companyName}
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        row.status === "new"
                          ? "Nouvelle"
                          : row.status === "contacted"
                            ? "Contactée"
                            : "Terminée"
                      }
                      color={
                        row.status === "new"
                          ? "error"
                          : row.status === "contacted"
                            ? "warning"
                            : "success"
                      }
                    />
                  </Stack>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: "repeat(3, minmax(0, 1fr))",
                      },
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Contact
                      </Typography>
                      <Typography fontWeight={700}>
                        {row.contactName}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Coordonnées
                      </Typography>
                      <Typography sx={{ wordBreak: "break-word" }}>
                        {row.email} · {row.phone || "sans téléphone"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Localisation
                      </Typography>
                      <Typography>
                        {row.city || "Ville non précisée"}
                      </Typography>
                    </Box>
                  </Box>
                  {row.message && (
                    <Paper
                      variant="outlined"
                      sx={{ mt: 2, p: 1.5, bgcolor: "grey.50" }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Message
                      </Typography>
                      <Typography sx={{ whiteSpace: "pre-line" }}>
                        {row.message}
                      </Typography>
                    </Paper>
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1.5 }}
                  >
                    Reçue le{" "}
                    {new Intl.DateTimeFormat("fr-FR", {
                      dateStyle: "short",
                      timeStyle: "short",
                    }).format(new Date(row.createdAt))}
                  </Typography>
                </Box>
                <Divider />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => companyFromInquiry(row)}
                  >
                    Créer l’entreprise
                  </Button>
                  <TextField
                    select
                    size="small"
                    label="Suivi"
                    value={row.status}
                    onChange={(e) => setInquiryStatus(row.id, e.target.value)}
                    sx={{ minWidth: 150 }}
                  >
                    <MenuItem value="new">Nouvelle</MenuItem>
                    <MenuItem value="contacted">Contactée</MenuItem>
                    <MenuItem value="finished">Terminée</MenuItem>
                    {row.status === "closed" && (
                      <MenuItem value="closed">
                        Terminée (ancien statut)
                      </MenuItem>
                    )}
                  </TextField>
                  <Button
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteOutlineRoundedIcon />}
                    onClick={() => deleteInquiry(row)}
                  >
                    Supprimer
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog
        open={companyOpen}
        onClose={() => setCompanyOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle fontWeight={900}>
          {companyId ? "Modifier l’entreprise" : "Nouvelle entreprise"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Nom de l’entreprise"
                value={companyForm.name}
                onChange={changeCompany("name")}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="URL souhaitée (facultatif)"
                value={companyForm.slug}
                onChange={changeCompany("slug")}
                helperText="Ex. entreprise-dupont"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom du contact"
                value={companyForm.contactName}
                onChange={changeCompany("contactName")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="E-mail"
                value={companyForm.contactEmail}
                onChange={changeCompany("contactEmail")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Téléphone"
                value={companyForm.contactPhone}
                onChange={changeCompany("contactPhone")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Adresse"
                value={companyForm.address}
                onChange={changeCompany("address")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Code postal"
                value={companyForm.postalCode}
                onChange={changeCompany("postalCode")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Ville"
                value={companyForm.city}
                onChange={changeCompany("city")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Pays"
                value={companyForm.country}
                onChange={changeCompany("country")}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Notes internes"
                value={companyForm.notes}
                onChange={changeCompany("notes")}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompanyOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={saveCompany}
            disabled={!companyForm.name}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={collectionOpen}
        onClose={() => setCollectionOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle fontWeight={900}>
          {collectionId ? "Modifier la collecte" : "Créer une collecte"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Stepper activeStep={collectionStep} alternativeLabel>
              {["Informations", "Images", "Bilan"].map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {collectionStep === 0 && (
              <>
                <Alert severity="info">
                  Choisis l’entreprise et l’étape actuelle. Le lien public est
                  créé automatiquement à l’enregistrement.
                </Alert>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Entreprise"
                      value={collectionForm.companyId}
                      onChange={changeCollection("companyId")}
                      required
                    >
                      {companies.map((x) => (
                        <MenuItem key={x.id} value={x.id}>
                          {x.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Étape affichée"
                      value={collectionForm.status}
                      onChange={changeCollection("status")}
                    >
                      <MenuItem value="preparation">
                        Préparation · expliquer la collecte
                      </MenuItem>
                      <MenuItem value="active">Collecte en cours</MenuItem>
                      <MenuItem value="report">
                        Rapport publié · lien actif 30 jours
                      </MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Titre de la collecte"
                      value={collectionForm.title}
                      onChange={changeCollection("title")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="URL personnalisée"
                      value={collectionForm.slug}
                      onChange={changeCollection("slug")}
                      helperText="Créée automatiquement si vide"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Date de début"
                      InputLabelProps={{ shrink: true }}
                      value={collectionForm.startDate || ""}
                      onChange={changeCollection("startDate")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Date de fin"
                      InputLabelProps={{ shrink: true }}
                      value={collectionForm.endDate || ""}
                      onChange={changeCollection("endDate")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Emplacement de la caisse"
                      value={collectionForm.locationLabel}
                      onChange={changeCollection("locationLabel")}
                      placeholder="Hall d’accueil, cafétéria…"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      label="Introduction"
                      value={collectionForm.introText}
                      onChange={changeCollection("introText")}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      label="Consignes pour les collaborateurs"
                      value={collectionForm.employeeMessage}
                      onChange={changeCollection("employeeMessage")}
                    />
                  </Grid>
                </Grid>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 3, bgcolor: "#f7fbf8" }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Lien public de l’entreprise
                  </Typography>
                  {collectionForm.slug ? (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems={{ sm: "center" }}
                      gap={1}
                    >
                      <Typography
                        color={GREEN}
                        fontWeight={800}
                        sx={{ flex: 1, wordBreak: "break-all" }}
                      >
                        {publicUrl(collectionForm.slug)}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<ContentCopyRoundedIcon />}
                        onClick={() => copy(collectionForm.slug)}
                      >
                        Copier
                      </Button>
                    </Stack>
                  ) : (
                    <Typography color="text.secondary">
                      L’adresse sera générée automatiquement lors de
                      l’enregistrement et affichée immédiatement dans le panel.
                    </Typography>
                  )}
                </Paper>
              </>
            )}
            {collectionStep === 1 && (
              <>
                <Alert severity="info">
                  Les images sont facultatives. Tu peux enregistrer la collecte
                  maintenant et les ajouter plus tard.
                </Alert>
                <Typography variant="h6" fontWeight={900}>
                  Images de la collecte
                </Typography>
                <Grid container spacing={2}>
                  {[
                    ["heroImageUrl", "Image principale"],
                    ["collectionImageUrl", "Image du point de collecte"],
                    ["reportImageUrl", "Image du rapport"],
                  ].map(([field, label]) => (
                    <Grid item xs={12} md={4} key={field}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                        <Box
                          sx={{
                            height: 130,
                            borderRadius: 2,
                            bgcolor: "#edf7f0",
                            backgroundImage: collectionForm[field]
                              ? `url(${collectionForm[field]})`
                              : "none",
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            display: "grid",
                            placeItems: "center",
                          }}
                        >
                          {!collectionForm[field] && (
                            <Typography color="text.secondary">
                              Zone image
                            </Typography>
                          )}
                        </Box>
                        <Typography fontWeight={800} sx={{ my: 1 }}>
                          {label}
                        </Typography>
                        <Button
                          component="label"
                          startIcon={<UploadRoundedIcon />}
                          disabled={uploading === field}
                        >
                          Choisir une image
                          <input
                            hidden
                            type="file"
                            accept="image/*"
                            onChange={(e) => uploadImage(e, field)}
                          />
                        </Button>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
            {collectionStep === 2 && (
              <>
                <Typography variant="h6" fontWeight={900}>
                  Bilan de la collecte
                </Typography>
                <Grid container spacing={2}>
                  {[
                    ["phonesCount", "Téléphones"],
                    ["tabletsCount", "Tablettes"],
                    ["laptopsCount", "Ordinateurs portables"],
                    ["otherDevicesCount", "Autres appareils"],
                    ["depollutedPartsCount", "Pièces dépolluées"],
                    ["recycledPartsCount", "Pièces recyclées"],
                    ["reusedPartsCount", "Pièces récupérées / réemployées"],
                  ].map(([field, label]) => (
                    <Grid item xs={6} md={3} key={field}>
                      <TextField
                        fullWidth
                        type="number"
                        label={label}
                        value={collectionForm[field]}
                        inputProps={{ min: 0 }}
                        onChange={changeCollection(field)}
                      />
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      label="Message final du rapport"
                      value={collectionForm.reportMessage}
                      onChange={changeCollection("reportMessage")}
                    />
                  </Grid>
                </Grid>
                <Alert severity="info">
                  Le passage à « Rapport publié » démarre automatiquement la
                  disponibilité publique de 30 jours. Après ce délai, le lien
                  renvoie vers l’accueil de collect.123back.eu.
                </Alert>
                {collectionForm.status === "report" && (
                  <Alert severity="warning">
                    En enregistrant cette étape, le bilan devient public et sa
                    période de disponibilité de 30 jours commence immédiatement.
                    Vérifie les chiffres et le message final avant de continuer.
                  </Alert>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCollectionOpen(false)}>Annuler</Button>
          {collectionStep > 0 && (
            <Button onClick={() => setCollectionStep((step) => step - 1)}>
              Précédent
            </Button>
          )}
          {collectionStep < 2 && (
            <Button
              variant="outlined"
              onClick={() => setCollectionStep((step) => step + 1)}
              disabled={!collectionForm.companyId}
            >
              Suivant
            </Button>
          )}
          <Button
            variant="contained"
            sx={{ bgcolor: GREEN }}
            onClick={saveCollection}
            disabled={!collectionForm.companyId}
          >
            {collectionForm.status === "report"
              ? "Enregistrer et publier le bilan"
              : "Enregistrer"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
